import React, {useEffect, useState} from 'react';
import {IonIcon} from '@ionic/react';
import {closeOutline, cloudDownloadOutline, sparklesOutline} from 'ionicons/icons';
import './UpdateAvailableButton.css';

interface UpdateButtonProps {
    onUpdate: () => void;
    isVisible: boolean;
    message?: string;
    installedVersionLabel?: string;
    availableVersionLabel?: string;
    canApplyUpdate?: boolean;
}

/** One app-level update prompt. Individual pages must not register their own SW listeners. */
const UpdateAvailableButton: React.FC<UpdateButtonProps> = ({
    onUpdate,
    isVisible,
    message = 'Er staat een nieuwe versie voor je klaar',
    installedVersionLabel,
    availableVersionLabel,
    canApplyUpdate = true,
}) => {
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (isVisible) {
            setDismissed(false);
        }
    }, [availableVersionLabel, isVisible]);

    if (!isVisible || dismissed) {
        return null;
    }

    const actionLabel = canApplyUpdate ? 'Nu bijwerken' : 'Update voorbereiden';

    return (
        <aside className="update-prompt" role="status" aria-live="polite">
            <div className="update-prompt__card">
                <div className="update-prompt__glow" aria-hidden="true"/>
                <div className="update-prompt__icon" aria-hidden="true">
                    <IonIcon icon={sparklesOutline}/>
                </div>
                <div className="update-prompt__copy">
                    <span className="update-prompt__eyebrow">Een nieuwe versie is gedropt</span>
                    <strong>{message}</strong>
                    <p>{canApplyUpdate
                        ? 'Werk veilig bij en laad direct de nieuwste verbeteringen.'
                        : 'We hebben een nieuwere versie gezien. Zet hem met één tik klaar.'}
                    </p>
                    {(installedVersionLabel || availableVersionLabel) && (
                        <div className="update-prompt__versions">
                            {installedVersionLabel && <span>Nu: {installedVersionLabel}</span>}
                            {availableVersionLabel && <span>Nieuw: {availableVersionLabel}</span>}
                        </div>
                    )}
                </div>
                <div className="update-prompt__actions">
                    <button className="update-prompt__primary" type="button" onClick={onUpdate}>
                        <IonIcon icon={cloudDownloadOutline}/>
                        {actionLabel}
                    </button>
                    <button className="update-prompt__dismiss" type="button" onClick={() => setDismissed(true)}>
                        Later
                    </button>
                </div>
                <button className="update-prompt__close" type="button" onClick={() => setDismissed(true)} aria-label="Updatebericht sluiten">
                    <IonIcon icon={closeOutline}/>
                </button>
            </div>
        </aside>
    );
};

export default UpdateAvailableButton;
